'use client'

import { i18nConfig } from '@/i18n/i18n.config'
import { useUpdateProfileMutation } from 'graphql/profile/update.profile'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store'
import { captureException } from '@tucan/utils/error-tracking'

/**
 * The locale lives in the route (`/[lang]/...`) and every render syncs i18next
 * back to it (see `@/i18n/client`). Changing the language therefore has to move
 * the browser to the matching locale prefix - a plain `i18n.changeLanguage()`
 * is reverted as soon as anything re-syncs with the URL.
 */
export const useChangeLanguage = () => {
  const { i18n } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const authenticated = useAppSelector(state => state.auth.authenticated)
  const [updateProfile] = useUpdateProfileMutation()

  return useCallback(async (lang: string, options: { persist?: boolean } = {}) => {
    await i18n.changeLanguage(`${lang}`)

    if (lang === 'cimode') {
      // this is to force the new value to persist even after a reload!
      localStorage.setItem(i18nConfig.cookieName, 'cimode')
      return
    }

    if (authenticated && options.persist !== false) {
      updateProfile({
        variables: {
          profile: {
            lang,
          },
        },
      }).catch(e => {
        captureException(e)
      })
    }

    const segments = `${pathname}`.split('/')

    if (!i18nConfig.locales.includes(segments[1])) {
      return
    }

    segments[1] = lang

    const query = searchParams?.toString()

    router.replace(`${segments.join('/')}${query ? `?${query}` : ''}`)
  }, [i18n, authenticated, updateProfile, pathname, searchParams, router])
}
